import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { C, LOGO_SEAL_WHITE, AppContext } from "../App";
import { toRoman } from "../utils";

const COL_HEADER = {
  display: "block",
  fontFamily: "'EB Garamond', serif",
  fontSize: 11,
  fontWeight: 400,
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  color: "var(--gold-deep)",
  paddingBottom: 10,
  borderBottom: "1px solid var(--rule)",
  marginBottom: 18,
};

const LINK_BTN = {
  display: "block",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "'EB Garamond', serif",
  fontSize: 14,
  color: "var(--ink-soft)",
  letterSpacing: "0.02em",
  padding: "3px 0",
  textAlign: "left",
};

const STATIC_ITEM = {
  display: "block",
  fontFamily: "'EB Garamond', serif",
  fontSize: 14,
  color: "var(--ink-mute)",
  letterSpacing: "0.02em",
  padding: "3px 0",
};

const COLOPHON_TEXT = {
  fontFamily: "'EB Garamond', serif",
  fontSize: 10,
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  color: "var(--ink-mute)",
};

export default function Footer() {
  const { openEnquiry } = useContext(AppContext);
  const navigate = useNavigate();

  // Hash links (e.g. /about#founder) are scrolled into place by the app-level
  // ScrollToTop handler once the target section mounts; jumping to the top
  // here first would just cause a visible flash before it corrects itself.
  const goTo = (path) => {
    navigate(path);
    if (!path.includes("#")) window.scrollTo(0, 0);
  };

  return (
    <footer style={{
      background: C.bgRaised,
      borderTop: `1px solid ${C.ink}`,
      marginTop: 80,
      // Additive clearance for Safari's translucent bottom bar/home indicator on
      // notched devices — applied to the footer itself (the single shared
      // element every page ends in) rather than any inner row, so the extra
      // space still renders with the footer's own background and the colour
      // reaches the true screen edge. env() falls back to 0px everywhere else,
      // so desktop is unaffected. The mobile-only +24px buffer (Safari's
      // compact floating toolbar is taller than the raw safe-area inset alone)
      // is added via the media query below, scoped so it can't add space on
      // desktop, where this padding-bottom would otherwise just be 0px anyway
      // but the flat +24px constant isn't viewport-aware on its own.
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
    }}>

      {/* ── Letterhead row ── */}
      <style>{`
        @media (max-width: 599px) {
          .footer-letterhead-row {
            grid-template-columns: 1fr !important;
            text-align: center !important;
            justify-items: center !important;
          }
          .footer-letterhead-row > div { text-align: center !important; }
        }
        @media (max-width: 767px) {
          footer { padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 24px) !important; }
        }
      `}</style>
      <div style={{ padding: "48px clamp(20px, 5vw, 64px) 36px" }}>
        <div className="footer-letterhead-row" style={{
          maxWidth: 1180, margin: "0 auto",
          display: "grid", gridTemplateColumns: "auto 1fr auto",
          gap: 24, alignItems: "center",
        }}>
          {/* Seal — gold-deep, matching the "Anno 1627" tone. LOGO_SEAL_WHITE is a
              static SVG loaded via <img>, so it can't inherit currentColor the way an
              inlined SVG could; masking it onto a solid-colour div gives an exact,
              non-approximated match to --gold-deep instead of a filter-tuning guess. */}
          <div
            role="img"
            aria-label=""
            style={{
              width: 72, height: 72,
              backgroundColor: "var(--gold-deep)",
              WebkitMaskImage: `url(${LOGO_SEAL_WHITE})`,
              maskImage: `url(${LOGO_SEAL_WHITE})`,
              WebkitMaskSize: "contain", maskSize: "contain",
              WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
              WebkitMaskPosition: "center", maskPosition: "center",
            }}
          />

          {/* Centre: wordmark only */}
          <div style={{ textAlign: "center" }}>
            <span style={{
              fontFamily: "'Cinzel', serif",
              fontSize: 18,
              fontWeight: 400,
              letterSpacing: "0.30em",
              textTransform: "uppercase",
              color: "var(--ink)",
            }}>ANACHRONA</span>
            <div style={{
              fontFamily: "'EB Garamond', serif",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-mute)",
              letterSpacing: "0.06em",
              marginTop: 8,
            }}>Heritage estate advisory and documentation.</div>
          </div>

          {/* Anno + motto */}
          <div style={{ textAlign: "right" }}>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 300,
              fontSize: 14,
              letterSpacing: "0.22em",
              color: "var(--gold-deep)",
            }}>
              Anno 1627
            </div>
            <div style={{
              fontFamily: "'EB Garamond', serif",
              fontSize: 11,
              color: "var(--ink-mute)",
              letterSpacing: "0.20em",
              marginTop: 4,
            }}>
              CHRISTVS · REGNAT
            </div>
          </div>
        </div>
      </div>

      {/* ── Navigation columns ── */}
      <div style={{ borderTop: `1px solid ${C.rule}`, padding: "0 clamp(20px, 5vw, 64px)" }}>
        <div style={{
          maxWidth: 1180, margin: "0 auto",
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 48, padding: "32px 0 0", marginBottom: 56,
        }}>

          {/* Column 1 — Services */}
          <div>
            <span style={COL_HEADER}>Services</span>
            <button style={LINK_BTN} onClick={() => goTo("/catalogue")}>Estates</button>
            <button style={LINK_BTN} onClick={() => goTo("/digital-twins")}>Documentation</button>
            <button style={LINK_BTN} onClick={() => goTo("/archive")}>The Archive</button>
          </div>

          {/* Column 2 — About */}
          <div>
            <span style={COL_HEADER}>About</span>
            <button style={LINK_BTN} onClick={() => goTo("/about")}>About</button>
            <button style={LINK_BTN} onClick={() => goTo("/about#founder")}>The Founder</button>
          </div>

          {/* Column 3 — Correspondence */}
          <div>
            <span style={COL_HEADER}>Correspondence</span>
            <button style={LINK_BTN} onClick={() => openEnquiry("General")}>Enquiries</button>
            <button style={LINK_BTN} onClick={() => openEnquiry("General")}>Private viewings</button>
            <span style={STATIC_ITEM}>info@anachrona.art</span>
            <button style={LINK_BTN} onClick={() => goTo("/privacy")}>Privacy</button>
          </div>

          {/* Column 4 — Registered Office */}
          <div>
            <span style={COL_HEADER}>Registered Office</span>
            <span style={STATIC_ITEM}>Anachrona, Unipessoal Lda</span>
            <span style={STATIC_ITEM}>Lourinhã, Costa de Prata</span>
            <span style={STATIC_ITEM}>Portugal · NIPC 517883988</span>
          </div>

        </div>
      </div>

      {/* ── Colophon ── */}
      <div style={{ borderTop: `1px solid ${C.rule}`, padding: "0 clamp(20px, 5vw, 64px)" }}>
        <div style={{
          maxWidth: 1180, margin: "0 auto",
          display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16,
          paddingTop: 24, paddingBottom: 24,
        }}>
          <span style={COLOPHON_TEXT}>Lourinhã, Portugal</span>
          <span style={COLOPHON_TEXT}>© {toRoman(new Date().getFullYear())} Anachrona</span>
        </div>
      </div>

    </footer>
  );
}
